import { useEffect, useMemo, useRef, useState } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { format } from 'date-fns'
import { AvailableModules } from 'generated/featureDefinitions'
import type { AllProductUpdatesQuery, HeaderViewsQuery } from 'generated/resources/graphql'
import {
  AllProductUpdatesDocument,
  CreateProductUpdateHeaderViewDocument,
  HeaderViewsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { useGetProductUpdates } from 'modules/product_updates/hooks/useGetProductUpdates'

import { BuildCrossSelling } from './buildCrossSelling'
import { getModuleReadableName, moduleMapper } from './moduleMappers'
import { CrossSellingModalState } from './types'
import { useGetAvailableModules } from './useGetAvailableModules'

export type { CrossSellingModalState }

type ProductUpdatesHeaderView =
  HeaderViewsQuery['productUpdates']['headerViewsConnection']['edges'][number]['node']

type ProductUpdatesUpdate =
  AllProductUpdatesQuery['productUpdates']['updatesConnection']['edges'][number]['node']

/**
 * Returns the mapped module identifier or the original if not found.
 */
function getModuleIdentifier(moduleId: string) {
  return moduleMapper[moduleId] || 'core'
}

/**
 * Returns the last view date as a Date object, or undefined.
 */
function getLastViewDate(headerViews: ProductUpdatesHeaderView[] | undefined, moduleId: string) {
  const moduleIdentifier = getModuleIdentifier(moduleId)
  const moduleHeaderView = headerViews?.find((view) => moduleId && view.module === moduleIdentifier)
  return moduleHeaderView?.lastViewDate ? new Date(moduleHeaderView.lastViewDate) : undefined
}

/**
 * Returns true if there are unread product updates since the last view date.
 */
function hasUnreadProductUpdates(productUpdates: ProductUpdatesUpdate[], lastViewDate?: Date) {
  if (!lastViewDate) return true
  return productUpdates.some((update) => new Date(update.releaseDate) > lastViewDate)
}

/**
 * Builds the product updates items for the dropdown.
 */
function buildProductUpdatesItems(
  productUpdates: ProductUpdatesUpdate[],
  navigation: Navigation<NavigationPaths>,
  lastViewDate?: Date
) {
  return productUpdates.slice(0, 4).map((update) => {
    const href = update.navigationSlug
      ? navigation.discover.fullPath() + `/${update.navigationSlug}`
      : navigation.discover.productUpdates.item.fullPath({ slug: update.slug })
    return {
      title: update.title,
      href,
      mediaUrl: update.media?.at(0)?.url || update.thumbnail.src,
      updated: format(new Date(update.releaseDate), 'dd MMM yyyy'),
      unread: lastViewDate ? lastViewDate < new Date(update.releaseDate) : false,
    }
  })
}

export const useProductUpdatesHeader = (
  moduleId: string,
  setCrossSellingModal: (value: CrossSellingModalState) => void
) => {
  const { navigation } = useNavigation()
  const [crossSellingVisible, setCrossSellingVisible] = useState(false)
  const {
    company,
    access: { locale: user_locale },
  } = useCurrentSession()
  const mainCountry = company.legalEntities.find((legalEntity) => legalEntity.main)?.country

  const [viewUpsellProductCardComponent, setViewUpsellProductCardComponent] =
    useLocalStorage<string>(LOCAL_STORAGE_KEYS.viewUpsellProductCardComponent)
  const moduleIdentifier = getModuleIdentifier(moduleId)

  /***
   *
   * Product updates dropdown
   *
   * */ const { productUpdates: moduleProductUpdates } = useGetProductUpdates({
    moduleIdentifier,
    alreadyReleased: true,
  })

  const datoLocale = getDatoLocale(user_locale)
  const allUpdatesQuery = useQuery(AllProductUpdatesDocument, {
    variables: {
      moduleIdentifier: 'all',
      alreadyReleased: true,
      locale: datoLocale,
      preview: false,
    },
    fetchPolicy: 'cache-first',
  })
  const allProductUpdates = useMemo(
    () => allUpdatesQuery.data?.productUpdates.updatesConnection.edges?.map((e) => e.node) ?? [],
    [allUpdatesQuery.data]
  )

  const productUpdates = useMemo(() => {
    const seenIds = new Set(moduleProductUpdates.map((u) => u.id))
    const merged = [...moduleProductUpdates, ...allProductUpdates.filter((u) => !seenIds.has(u.id))]
    return merged.sort(
      (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
    )
  }, [moduleProductUpdates, allProductUpdates])

  const headerViewsQuery = useQuery(HeaderViewsDocument, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
  })

  const [createProductUpdateHeaderView] = useMutation(CreateProductUpdateHeaderViewDocument, {
    extractorFn: (data) => data.productUpdatesMutations.createProductupdatesHeaderView,
    refetchQueries: [HeaderViewsDocument],
    awaitRefetchQueries: true,
  })

  const dropdownIsVisible = productUpdates.length > 0
  const headerViews = headerViewsQuery.data?.productUpdates.headerViewsConnection.edges?.map(
    (edge) => edge.node
  )
  const lastViewDate = getLastViewDate(headerViews, moduleId)
  const hasUnreadUpdates = hasUnreadProductUpdates(productUpdates, lastViewDate)

  const handleCreateProductUpdateHeaderView = async (open: boolean) => {
    if (open) {
      track(trackingEvents.productUpdates.openProductUpdatesDropdown, {
        page_path: window.location.pathname,
      })
    }
    if (!moduleId) return
    await createProductUpdateHeaderView({
      variables: {
        module: moduleIdentifier || '',
      },
    })
  }

  /***
   *
   * CrossSelling modules
   *
   * */

  // Fallback to default modules if the module identifier is not found Ex. factorial-core
  const defaultFallback = ['benefits', 'reviews', 'trainings']

  // Edge case for timeoff module to match available modules
  const normalizedIdentifier =
    moduleIdentifier === 'time-off' ? 'timeoff' : moduleIdentifier.replace(/[-_]/g, '_')

  // Check if the normalized identifier is in the available modules if not return the default fallback
  const datoIdentifier = Object.values(AvailableModules)
    .map((module) => module.toString())
    .includes(normalizedIdentifier.toString())
    ? [moduleIdentifier]
    : defaultFallback

  const { data: datoModules } = useGetDatoModules({
    slugs: datoIdentifier,
    locale: datoLocale,
    countryCode: countryCode(mainCountry || ''),
  })

  const crossSellingModulesData =
    datoIdentifier != defaultFallback
      ? datoModules.at(0)?.crossSellingModules
      : datoModules || undefined

  const crossSellingModules = useGetAvailableModules(crossSellingModulesData, mainCountry || '')

  const hasFiredSeeDropdownRef = useRef<string | null>(null)

  useEffect(() => {
    if (moduleId && dropdownIsVisible && hasFiredSeeDropdownRef.current !== moduleId) {
      hasFiredSeeDropdownRef.current = moduleId
      track(trackingEvents.productUpdates.seeProductUpdatesDropdown, {
        module: moduleIdentifier,
        hasUnread: hasUnreadUpdates,
      })
    }
  }, [moduleId, dropdownIsVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (crossSellingModules.length > 0) {
      // check if the viewUpsellProductCardComponent exist or is not set or if the date is older than 30 days
      if (
        !viewUpsellProductCardComponent ||
        new Date(viewUpsellProductCardComponent).getTime() <
          new Date().getTime() - 30 * 24 * 60 * 60 * 1000
      ) {
        setCrossSellingVisible(true)
      } else {
        setCrossSellingVisible(false)
      }
    } else {
      setCrossSellingVisible(false)
    }
  }, [crossSellingModules, viewUpsellProductCardComponent])

  const moduleName = getModuleReadableName(moduleId || '')

  return {
    isVisible: dropdownIsVisible,
    label: i18n.t('product_updates.notification_widget.label', {
      module: moduleName,
    }),
    updatesPageUrl:
      navigation.discover.productUpdates.fullPath() + `?module=${moduleIdentifier || ''}`,
    moreUpdatesLabel: i18n.t('product_updates.notification_widget.more_updates'),
    getUpdates: async () => buildProductUpdatesItems(productUpdates, navigation, lastViewDate),
    hasUnread: hasUnreadUpdates,
    emptyScreen: {
      title: i18n.t('product_updates.notification_widget.empty_screen.title', {
        product_name: moduleName,
      }),
      description: i18n.t('product_updates.notification_widget.empty_screen.description'),
      buttonText: i18n.t('product_updates.notification_widget.empty_screen.button_text'),
    },
    errorScreen: {
      title: i18n.t('product_updates.notification_widget.error_screen.title'),
      description: i18n.t('product_updates.notification_widget.error_screen.description'),
      buttonText: i18n.t('product_updates.notification_widget.error_screen.button_text'),
    },
    onOpenChange: (open: boolean) => {
      handleCreateProductUpdateHeaderView(open)
    },
    onHeaderClick: () => {
      track(trackingEvents.productUpdates.goToProductUpdatesPage, {
        source: 'product_updates_dropdown',
      })
    },
    onItemClick: () => {
      track(trackingEvents.productUpdates.goToProductUpdatesDetailPage, {
        source: 'product_updates_dropdown',
      })
    },
    currentModule: moduleName,
    crossSelling: BuildCrossSelling({
      moduleId,
      crossSellingModules: crossSellingModules || undefined,
      setCrossSellingModal,
      crossSellingVisible,
      setCrossSellingVisible,
      setViewUpsellProductCardComponent,
      navigation,
      companyId: company.id,
    }),
  }
}
